import axios from 'axios'
import env, { SUPPORTED_ENGINE_VERSION } from './env'
import { getFeathersEventManager } from '@taxfyle/web-commons/lib/misc/APIClient'
import { JobsV3API } from 'jobs/API'
import { enableDevTools } from '@taxfyle/web-commons/lib/utils/grpcUtil'
import { createGrpcAuthenticator } from '@taxfyle/web-commons/lib/misc/grpc-authenticator'
import memoize from 'memoizee'
import { ConsultationServicePromiseClient } from '@taxfyle/api-internal/internal/consultation_grpc_web_pb'
import { SetConsultationAvailabilityRequest } from '@taxfyle/api-internal/internal/consultation_pb'
import { Interval } from '@taxfyle/api-internal/shared/types/interval_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { InfoGatheringServicePromiseClient } from '@taxfyle/api-internal/internal/info_gathering_grpc_web_pb'
import {
  GetChecklistRequest,
  ProvideSupportingItemDocumentRequest,
  RemoveSupportingItemDocumentRequest,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { ListJobEventsRequest } from '@taxfyle/api-internal/internal/job_event_pb'
import { JobEventsServicePromiseClient } from '@taxfyle/api-internal/internal/job_event_grpc_web_pb'
import { JobRatingServicePromiseClient } from '@taxfyle/api-internal/internal/job_rating_grpc_web_pb'
import { getFeatureToggleClient } from 'misc/featureToggles'
import {
  GetJobRatingStatusRequest,
  SubmitProRatingRequest,
  SubmitPlatformRatingRequest,
} from '@taxfyle/api-internal/internal/job_rating_pb'

import { interval, EMPTY } from 'rxjs'
import {
  map,
  switchMap,
  filter,
  share,
  startWith,
  distinctUntilChanged,
} from 'rxjs/operators'

export default class TaxfyleAPI {
  constructor({ authStore, workRealtime$, togglesInitialized$ }) {
    this.authStore = authStore
    this.jobs = new JobsAPI(this)
    this.jobsV3 = new JobsV3API(
      {
        baseURL: env.WORK_DOTNET_API,
        token$: authStore.token$,
      },
      workRealtime$,
      togglesInitialized$
    )
    this.providers = new ProviderAPI(this)
    this.jobEvents = new JobEventsAPI(this)
    this.jobEventsV3 = new JobEventsV3API(
      {
        baseURL: env.WORK_DOTNET_API,
        token$: authStore.token$,
      },
      workRealtime$,
      togglesInitialized$
    )
    this.legends = new LegendsAPI(this)
    this.documentTypes = new DocumentTypeAPI(this)

    this.consultations = new ConsultationAPI({
      baseURL: env.WORK_DOTNET_API,
      token$: authStore.token$,
    })

    this.infoGathering = new InfoGatheringAPI(
      {
        baseURL: env.WORK_DOTNET_API,
        token$: authStore.token$,
      },
      workRealtime$
    )

    this.jobRating = new JobRatingAPI({
      baseURL: env.WORK_DOTNET_API,
      token$: authStore.token$,
    })
  }

  origin = window.location.origin

  get workAPIClient() {
    const client = this.client(env.JOB_API)
    client.interceptors.request.use((config) => {
      config.params ??= {}
      config.params.supported_engine_version = SUPPORTED_ENGINE_VERSION
      return config
    })
    return client
  }

  get localClient() {
    return this.client()
  }

  get user() {
    return this.authStore.user
  }

  client(baseUrl) {
    return axios.create({
      baseURL: baseUrl.startsWith('/')
        ? `${window.location.origin}${baseUrl}`
        : baseUrl,
      headers: {
        Authorization: `Bearer ${this.authStore.token}`,
      },
    })
  }
}

class APIClient {
  constructor(api) {
    this.api = api
  }

  on(eventName, handler) {
    this.api.authStore.onLogin(async () => {
      getFeathersEventManager(
        this.api.workAPIClient.defaults.baseURL,
        this.api.authStore.token,
        {
          supported_engine_version: SUPPORTED_ENGINE_VERSION,
        }
      ).subscribe(this.service, eventName, handler)
    })
  }
}

class ProviderAPI extends APIClient {
  service = 'job-providers'

  get(id) {
    return this.api.workAPIClient.get(`/job-providers/${id}`).then(_data)
  }

  find(params) {
    params = { ...params }
    return this.api.workAPIClient.get(`/job-providers`, { params }).then(_data)
  }

  patch(id, params) {
    params = { ...params, status: 'UNCLAIMED' }
    return this.api.workAPIClient
      .patch(`/job-providers/${id}`, params)
      .then(_data)
  }
}

class JobsAPI extends APIClient {
  service = 'jobs'

  get(id) {
    return this.api.workAPIClient.get(`/jobs/${id}`).then(_data)
  }

  find(params) {
    params = { $limit: 100, ...params }
    return this.api.workAPIClient.get('/jobs', { params }).then(_data)
  }

  create(data) {
    return this.api.workAPIClient.post('/jobs', data).then(_data)
  }

  patch(id, data) {
    return this.api.workAPIClient.patch(`/jobs/${id}`, data).then(_data)
  }

  submit(id) {
    return this.patch(id, { status: 'UNCLAIMED' }).then(_data)
  }

  delete(id) {
    return this.api.workAPIClient.delete(`/jobs/${id}`).then(_data)
  }

  price(id) {
    return this.api.workAPIClient.get(`/job-price/${id}`).then(_data)
  }

  resubmitJob(id) {
    return this.api.workAPIClient.post(`/job/${id}/duplicate-job`).then(_data)
  }

  providePaymentInfo(jobId, params) {
    return this.api.workAPIClient
      .post(`/jobs/${jobId}/payment-info`, params)
      .then(_data)
  }
}

class JobEventsAPI extends APIClient {
  service = 'job-events'

  find(query) {
    return this.api.workAPIClient
      .get('/job-events', { params: query })
      .then(_data)
  }
}

class JobEventsV3API {
  constructor(opts, events$, togglesInitialized$) {
    this.client = createJobEventsGrpcClient(opts.baseURL)
    this.grpcAuthenticator = createGrpcAuthenticator({ token$: opts.token$ })

    this.events$ =
      events$ && togglesInitialized$
        ? togglesInitialized$.pipe(
            switchMap(() =>
              interval(10_000).pipe(
                startWith(this.useDotnetRealtimeEvents()),
                map(() => this.useDotnetRealtimeEvents()),
                distinctUntilChanged()
              )
            ),
            switchMap((isActive) => (isActive ? events$ : EMPTY)),
            filter(
              (event) =>
                event.getTypeCase() ===
                RealtimeEvent.TypeCase.JOB_EVENT_RECORDED
            ),
            share()
          )
        : null
  }

  /**
   * Lists job events.
   *
   * @param {string} jobId
   * @param {number} limit
   * @param {string} after
   *
   * @returns {Promise<{ jobEventsList: JobEventDto[], nextPageToken: string }>}
   */
  async listJobEvents({ jobId, limit, after }) {
    const request = new ListJobEventsRequest()
      .setJobId(jobId)
      .setPageSize(limit)
      .setPageToken(after)

    const response = await this.client.listJobEvents(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }

  /**
   * Checks whether to use realtime events using DotNET API.
   *
   * @returns {boolean}
   */
  useDotnetRealtimeEvents() {
    return getFeatureToggleClient().variation(
      'Portals.UseV3RealtimeEvents',
      false
    )
  }
}

class LegendsAPI extends APIClient {
  get client() {
    return this.api.client(env.LEGEND_API)
  }

  find({ workspace_id, ...query }) {
    return this.client
      .get(`/v3/workspaces/${workspace_id}/legends`, { params: query })
      .then(_data)
  }

  get(id) {
    return this.client.get(`/v3/legends/${id}`).then(_data)
  }

  getSettings(payload) {
    return this.client
      .post(`/v3/legends/id-query/settings`, payload)
      .then(_data)
  }

  getVersion(id, version, params) {
    return this.client
      .get(`/v3/legends/${id}${version ? `/versions/${version}` : ''}`, {
        ...params,
      })
      .then(_data)
  }
}

const createConsultationGrpcClient = memoize((baseUrl) => {
  const client = new ConsultationServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

const createJobEventsGrpcClient = memoize((baseUrl) => {
  const client = new JobEventsServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

class ConsultationAPI {
  constructor(opts) {
    this.client = createConsultationGrpcClient(opts.baseURL)
    this.grpcAuthenticator = createGrpcAuthenticator({ token$: opts.token$ })
  }

  async setConsultationAvailability({ jobId, availability }) {
    const availabilityIntervals = availability.map((a) =>
      new Interval()
        .setStartTime(Timestamp.fromDate(a.startTime))
        .setEndTime(Timestamp.fromDate(a.endTime))
    )
    const request = new SetConsultationAvailabilityRequest()
      .setJobId(jobId)
      .setAvailabilityList(availabilityIntervals)

    const response = await this.client.setConsultationAvailability(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }
}

const createInfoGatheringGrpcClient = memoize((baseUrl) => {
  const client = new InfoGatheringServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

class InfoGatheringAPI {
  constructor(opts, events$) {
    this.client = createInfoGatheringGrpcClient(opts.baseURL)
    this.grpcAuthenticator = createGrpcAuthenticator({ token$: opts.token$ })
    this.events$ = events$.pipe(
      filter((event) => {
        switch (event.getTypeCase()) {
          case RealtimeEvent.TypeCase.REQUESTED_SUPPORTING_ITEM_UPDATED:
            return true
        }

        return false
      })
    )
  }

  async getChecklist({ jobId }) {
    const request = new GetChecklistRequest().setJobId(jobId)

    const response = await this.client.getChecklist(
      request,
      await this.grpcAuthenticator.getMeta()
    )
    return response.toObject()
  }

  async provideSupportingItemDocument({ jobId, supportingItemId, documentId }) {
    const request = new ProvideSupportingItemDocumentRequest()
      .setId(supportingItemId)
      .setJobId(jobId)
      .setDocumentId(documentId)

    const response = await this.client.provideSupportingItemDocument(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }

  async removeSupportingItemDocument({ supportingItemId, documentId }) {
    const request = new RemoveSupportingItemDocumentRequest()
      .setId(supportingItemId)
      .setDocumentId(documentId)

    const response = await this.client.removeSupportingItemDocument(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }
}

const createJobRatingGrpcClient = memoize((baseUrl) => {
  const client = new JobRatingServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

class JobRatingAPI {
  constructor(opts) {
    this.client = createJobRatingGrpcClient(opts.baseURL)
    this.grpcAuthenticator = createGrpcAuthenticator({ token$: opts.token$ })
  }

  async getJobRatingStatus({ jobId }) {
    const request = new GetJobRatingStatusRequest().setJobId(jobId)
    const response = await this.client.getJobRatingStatus(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }

  async submitProRating({ jobId, rating, comment, proUserPublicId }) {
    const request = new SubmitProRatingRequest()
      .setJobId(jobId)
      .setRating(rating)
      .setComment(comment)
      .setProviderUserPublicId(proUserPublicId)
    const response = await this.client.submitProRating(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }

  async submitPlatformRating({ jobId, rating, comment, proUserPublicId }) {
    const request = new SubmitPlatformRatingRequest()
      .setJobId(jobId)
      .setRating(rating)
      .setComment(comment)
      .setProviderUserPublicId(proUserPublicId)
    const response = await this.client.submitPlatformRating(
      request,
      await this.grpcAuthenticator.getMeta()
    )

    return response.toObject()
  }
}

class DocumentTypeAPI extends APIClient {
  get client() {
    return this.api.client(env.DOCUMENT_API)
  }

  getMany(params) {
    return this.client.post('/v3/types/id-query', params).then(_data)
  }
}

function _data(response) {
  return response.data
}
